// frontend/src/pages/CartoesPage/CartoesPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCartoes, deleteCartao, getProximasFaturas, arquivarCartao, reativarCartao } from '../../services/cardService';
import CartaoModal from '../../components/CartaoModal/CartaoModal';
import LogoManager from '../../components/LogoManager/LogoManager';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { getErrorMessage } from '../../utils/errorHandler';

import { Button, Card, Row, Col, Statistic, Spin, Progress, Tooltip, List, Tag, Divider, Switch, Typography, message, Modal } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, CalendarOutlined, InboxOutlined, RollbackOutlined, RightOutlined } from '@ant-design/icons';

import './CartoesPage.css';

const { Title, Text } = Typography;

const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString + 'T00:00:00').toLocaleDateString('pt-BR', options);
};

const formatBRL = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const CartoesPage = () => {
    const [cartoes, setCartoes] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cartaoToEdit, setCartaoToEdit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);
    const navigate = useNavigate();

    const [proximasFaturas, setProximasFaturas] = useState([]);
    const [loadingFaturas, setLoadingFaturas] = useState(true);

    const [messageApi, contextHolder] = message.useMessage();
    const [modal, modalContextHolder] = Modal.useModal();

    // Hook de Responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchCartoes = useCallback(async () => {
        setLoading(true);
        try {
            const status = showArchived ? 'ARQUIVADO' : 'ATIVO';
            const data = await getCartoes({ status });
            setCartoes(data);
        } catch (err) {
            const msg = getErrorMessage(err, 'Falha ao carregar os cartões de crédito.');
            messageApi.error(msg);
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [showArchived, messageApi]);

    const fetchProximasFaturas = useCallback(async () => {
        try {
            setLoadingFaturas(true);
            const data = await getProximasFaturas();
            setProximasFaturas(data);
        } catch (err) {
            console.error("Falha ao carregar próximas faturas", err);
            const msg = getErrorMessage(err, 'Não foi possível carregar o painel de faturas.');
            messageApi.error(msg);
        } finally {
            setLoadingFaturas(false);
        }
    }, [messageApi]);

    useEffect(() => {
        fetchCartoes();
        if (!showArchived) {
            fetchProximasFaturas();
        }
    }, [fetchCartoes, fetchProximasFaturas, showArchived]);

    const { limiteTotal, limiteUtilizadoTotal, limiteDisponivelTotal } = useMemo(() => {
        const cartoesAtivos = showArchived ? [] : cartoes;
        const limiteTotal = cartoesAtivos.reduce((acc, cartao) => acc + parseFloat(cartao.limite || 0), 0);
        const limiteDisponivelTotal = cartoesAtivos.reduce((acc, cartao) => acc + parseFloat(cartao.limite_disponivel || 0), 0);
        const limiteUtilizadoTotal = limiteTotal - limiteDisponivelTotal;

        return { limiteTotal, limiteUtilizadoTotal, limiteDisponivelTotal };
    }, [cartoes, showArchived]);

    const handleOpenModal = (cartao = null) => {
        setCartaoToEdit(cartao);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCartaoToEdit(null);
    };

    const handleSave = (shouldClose = true, successMsg = null) => {
        if (successMsg) messageApi.success(successMsg);

        fetchCartoes();
        fetchProximasFaturas();

        if (shouldClose) {
            handleCloseModal();
        }
    };

    const handleAction = async (action, cartaoId, successMessageOrBuilder) => {
        try {
            const result = await action(cartaoId);
            const successMessage = typeof successMessageOrBuilder === 'function'
                ? successMessageOrBuilder(result)
                : successMessageOrBuilder;
            messageApi.success(successMessage);
            fetchCartoes();
        } catch (err) {
            const errorMsg = getErrorMessage(err, 'Ocorreu um erro ao executar a ação.');
            messageApi.error(errorMsg);
        }
    };

    const showConfirmArquivar = (cartao, hasTransactions) => {
        const actionVerb = hasTransactions ? 'arquivar' : 'excluir';

        const actionFunction = () => handleAction(
            deleteCartao,
            cartao.id,
            (result) => result?.archived
                ? 'Cartão arquivado com sucesso!'
                : 'Cartão excluído com sucesso!'
        );

        modal.confirm({
            title: `Deseja ${actionVerb} este cartão?`,
            icon: <ExclamationCircleOutlined />,
            content: hasTransactions
                ? 'O cartão será movido para a lista de arquivados e seu histórico será mantido.'
                : 'Esta ação removerá o cartão permanentemente, pois ele não possui histórico.',
            okText: `Sim, ${actionVerb}`,
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: actionFunction,
        });
    };

    return (
        <div style={{ padding: isMobile ? 0 : '24px', paddingBottom: isMobile ? 0 : '2rem' }}>
            {contextHolder}
            {modalContextHolder}

            {/* Header Responsivo */}
            <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
                <Col xs={24} md={12}>
                    <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Meus Cartões</Title>
                </Col>
                <Col xs={24} md={12}>
                    <div style={{
                        display: 'flex',
                        gap: 16,
                        justifyContent: isMobile ? 'flex-start' : 'flex-end',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isMobile ? 'space-between' : 'flex-start',
                            background: isMobile ? '#fff' : 'transparent',
                            padding: isMobile ? '8px 12px' : 0,
                            borderRadius: isMobile ? '8px' : 0,
                            border: isMobile ? '1px solid var(--color-border, #E8E2D6)' : 'none'
                        }}>
                            <Text>Exibir arquivados</Text>
                            <Switch checked={showArchived} onChange={setShowArchived} />
                        </div>

                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenModal()}
                            size={isMobile ? 'large' : 'middle'}
                            block={isMobile}
                        >
                            Adicionar Cartão
                        </Button>
                    </div>
                </Col>
            </Row>

            {loading ? (
                <div className="brand-skeleton" style={{ padding: 24 }} aria-busy="true" aria-label="Carregando">
  <div className="brand-skeleton-line brand-skeleton-line--lg" />
  <div className="brand-skeleton-line brand-skeleton-line--md" />
  <div className="brand-skeleton-line" />
  <div className="brand-skeleton-line brand-skeleton-line--sm" />
</div>
            ) : isMobile ? (
                <>
                    {!showArchived && (
                        <div className="cartoes-hero-mobile">
                            <div className="cartoes-hero-mobile__label">Limite disponível</div>
                            <div className="cartoes-hero-mobile__value" style={{ color: '#2F9E6F', marginBottom: 10 }}>
                                {formatBRL(limiteDisponivelTotal)}
                            </div>
                            <div className="cartoes-hero-mobile__row">
                                <div>
                                    <div className="cartoes-hero-mobile__label">Utilizado</div>
                                    <Text strong style={{ color: '#D64545' }}>{formatBRL(limiteUtilizadoTotal)}</Text>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div className="cartoes-hero-mobile__label">Limite total</div>
                                    <Text strong>{formatBRL(limiteTotal)}</Text>
                                </div>
                            </div>
                        </div>
                    )}
                    {cartoes.length === 0 ? (
                        <EmptyStateGuide
                            title={showArchived ? 'Nenhum cartão arquivado.' : 'Nenhum cartão cadastrado.'}
                            description={
                                showArchived
                                    ? 'Cartões arquivados aparecem aqui para preservar o histórico de faturas.'
                                    : 'Cadastre seus cartões e a conta usada para pagar a fatura, para controlar limites e vencimentos.'
                            }
                            actionLabel={showArchived ? undefined : 'Adicionar primeiro cartão'}
                            onAction={showArchived ? undefined : () => handleOpenModal()}
                        />
                    ) : (
                        <div className="dense-list" style={{ marginBottom: 16 }}>
                            {cartoes.map((cartao) => {
                                const disponivel = parseFloat(cartao.limite_disponivel || 0);
                                const limite = parseFloat(cartao.limite || 0);
                                const utilizado = Math.max(0, limite - disponivel);
                                const pct = limite > 0 ? Math.min(100, Math.round((utilizado / limite) * 100)) : 0;
                                const hasTransactions = Boolean(cartao.tem_movimentacao);
                                const nomeContaAssociada = cartao.conta_associada ? cartao.conta_associada.nome : 'N/A';
                                return (
                                    <div
                                        key={cartao.id}
                                        role="button"
                                        tabIndex={0}
                                        className={`dense-list__row${cartao.status === 'ARQUIVADO' ? ' dense-list__row--archived' : ''}`}
                                        onClick={() => navigate(`/cartoes/${cartao.id}`)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                navigate(`/cartoes/${cartao.id}`);
                                            }
                                        }}
                                    >
                                        <div className="dense-list__avatar">
                                            <LogoManager slug={cartao.bandeira} type="bandeira" />
                                        </div>
                                        <div className="dense-list__main">
                                            <div className="dense-list__title">{cartao.nome}</div>
                                            <div className="dense-list__sub">{nomeContaAssociada} · {pct}% usado</div>
                                        </div>
                                        <div className="dense-list__meta">
                                            <div className="dense-list__value" style={{ color: '#2F9E6F' }}>
                                                {formatBRL(disponivel)}
                                            </div>
                                            <div className="dense-list__hint">disponível</div>
                                            <div className="dense-list__actions">
                                                <button
                                                    type="button"
                                                    className="dense-list__action"
                                                    aria-label="Editar"
                                                    onClick={(e) => { e.stopPropagation(); handleOpenModal(cartao); }}
                                                >
                                                    <EditOutlined />
                                                </button>
                                                {cartao.status === 'ATIVO' ? (
                                                    <button
                                                        type="button"
                                                        className="dense-list__action dense-list__action--danger"
                                                        aria-label={hasTransactions ? 'Arquivar' : 'Excluir'}
                                                        onClick={(e) => { e.stopPropagation(); showConfirmArquivar(cartao, hasTransactions); }}
                                                    >
                                                        {hasTransactions ? <InboxOutlined /> : <DeleteOutlined />}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="dense-list__action dense-list__action--success"
                                                        aria-label="Reativar"
                                                        onClick={(e) => { e.stopPropagation(); handleAction(reativarCartao, cartao.id, 'Cartão reativado com sucesso!'); }}
                                                    >
                                                        <RollbackOutlined />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <RightOutlined className="dense-list__chevron" />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {!showArchived && proximasFaturas.length > 0 && (
                        <Card title="Faturas próximas" size="small" style={{ marginBottom: 16 }}>
                            <List
                                size="small"
                                dataSource={proximasFaturas.slice(0, 5)}
                                renderItem={(f) => (
                                    <List.Item
                                        className="fatura-proxima-item"
                                        style={{ cursor: 'pointer', padding: '8px 0' }}
                                        onClick={() => navigate(`/cartoes/${f.cartao_id || f.cartao?.id}`)}
                                    >
                                        <List.Item.Meta
                                            title={f.cartao_nome || f.cartao?.nome}
                                            description={`Vence ${formatDate(f.data_vencimento)}`}
                                        />
                                        <Text strong type="danger">{formatBRL(f.valor_total)}</Text>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    )}
                </>
            ) : (
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={16}>
                        <Row gutter={[24, 24]}>
                            {cartoes.length === 0 ? (
                                <Col span={24}>
                                    <EmptyStateGuide
                                        title={showArchived ? 'Nenhum cartão arquivado.' : 'Nenhum cartão cadastrado.'}
                                        description={
                                            showArchived
                                                ? 'Cartões arquivados aparecem aqui para preservar o histórico de faturas.'
                                                : 'Cadastre seus cartões e a conta usada para pagar a fatura, para controlar limites e vencimentos.'
                                        }
                                        actionLabel={showArchived ? undefined : 'Adicionar primeiro cartão'}
                                        onAction={showArchived ? undefined : () => handleOpenModal()}
                                    />
                                </Col>
                            ) : (
                            cartoes.map(cartao => {
                                const limite = parseFloat(cartao.limite || 0);
                                const disponivel = parseFloat(cartao.limite_disponivel || 0);
                                const utilizado = Math.max(0, limite - disponivel);
                                const percentualUtilizado = limite > 0 ? Math.min(100, Math.round((utilizado / limite) * 100)) : 0;
                                const melhorDiaCompra = cartao.dia_fechamento + 1;
                                const nomeContaAssociada = cartao.conta_associada ? cartao.conta_associada.nome : 'N/A';

                                const hasTransactions = Boolean(cartao.tem_movimentacao);
                                const cardActions = [];

                                cardActions.push(
                                    <Tooltip title="Editar detalhes do cartão">
                                        <div onClick={(e) => { e.stopPropagation(); handleOpenModal(cartao); }} className="card-action-btn">
                                            <EditOutlined key="edit" style={{ fontSize: '20px' }} />
                                            <span style={{ marginLeft: 8 }}>Editar</span>
                                        </div>
                                    </Tooltip>
                                );

                                if (cartao.status === 'ATIVO') {
                                    cardActions.push(
                                        <Tooltip title={hasTransactions ? "Arquivar cartão" : "Excluir cartão permanentemente"}>
                                            <div onClick={(e) => { e.stopPropagation(); showConfirmArquivar(cartao, hasTransactions); }} className="card-action-btn danger">
                                                {hasTransactions ? (
                                                    <>
                                                        <InboxOutlined key="archive" style={{ fontSize: '20px' }} />
                                                        <span style={{ marginLeft: 8 }}>Arquivar</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <DeleteOutlined key="delete" style={{ fontSize: '20px' }} />
                                                        <span style={{ marginLeft: 8 }}>Excluir</span>
                                                    </>
                                                )}
                                            </div>
                                        </Tooltip>
                                    );
                                } else {
                                    cardActions.push(
                                        <Tooltip title="Reativar cartão para uso">
                                            <div onClick={(e) => { e.stopPropagation(); handleAction(reativarCartao, cartao.id, 'Cartão reativado com sucesso!'); }} className="card-action-btn success">
                                                <RollbackOutlined key="reactivate" style={{ fontSize: '20px' }} />
                                                <span style={{ marginLeft: 8 }}>Reativar</span>
                                            </div>
                                        </Tooltip>
                                    );
                                }

                                return (
                                    <Col xs={24} md={12} key={cartao.id}>
                                        <Card
                                            hoverable
                                            onClick={() => navigate(`/cartoes/${cartao.id}`)}
                                            className={`cartao-card ${cartao.status === 'ARQUIVADO' ? 'archived' : ''}`}
                                            actions={cardActions}
                                        >
                                            <Card.Meta
                                                avatar={<LogoManager slug={cartao.bandeira} type="bandeira" />}
                                                title={cartao.nome}
                                                description={nomeContaAssociada}
                                            />
                                            <div className="cartao-card-body">
                                                <div className="cartao-limite-valores">
                                                    <div className="cartao-limite-item">
                                                        <Text type="secondary" className="cartao-limite-label">Utilizado</Text>
                                                        <Text strong className="cartao-limite-valor cartao-limite-valor--usado">
                                                            {formatBRL(utilizado)}
                                                        </Text>
                                                    </div>
                                                    <div className="cartao-limite-item cartao-limite-item--right">
                                                        <Text type="secondary" className="cartao-limite-label">Disponível</Text>
                                                        <Text strong className="cartao-limite-valor cartao-limite-valor--disponivel">
                                                            {formatBRL(disponivel)}
                                                        </Text>
                                                    </div>
                                                </div>
                                                <div className="limit-progress-container">
                                                    <Text className="cartao-limite-pct">{percentualUtilizado}% Utilizado</Text>
                                                    <Progress
                                                        percent={percentualUtilizado}
                                                        showInfo={false}
                                                        size="small"
                                                        strokeColor={{
                                                            '0%': '#2F9E6F',
                                                            '50%': '#B89040',
                                                            '100%': '#D64545',
                                                        }}
                                                    />
                                                </div>
                                                <div className="cartao-detalhes">
                                                    <span>Fecha dia {cartao.dia_fechamento}</span>
                                                    <span>Vence dia {cartao.dia_vencimento}</span>
                                                </div>
                                                <div className="cartao-detalhes-extra">
                                                    <Tag icon={<CalendarOutlined />} color="gold">
                                                        Melhor compra: dia {melhorDiaCompra}
                                                    </Tag>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })
                            )}
                        </Row>
                    </Col>

                    <Col xs={24} lg={8}>
                        {!showArchived && (
                            <div className="summary-sidebar">
                                <Card title="Resumo Geral" style={{ marginBottom: '24px' }}>
                                    <Statistic title="Limite Total (Todos Cartões) " value={limiteTotal} formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)} />
                                    <Divider style={{ margin: '12px 0' }} />
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Statistic title="Utilizado" value={limiteUtilizadoTotal} formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)} valueStyle={{ color: '#D64545', fontSize: '1.2rem' }} />
                                        </Col>
                                        <Col span={12}>
                                            <Statistic title="Disponível" value={limiteDisponivelTotal} formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)} valueStyle={{ color: '#2F9E6F', fontSize: '1.2rem' }} />
                                        </Col>
                                    </Row>
                                </Card>

                                <Card title="Faturas Próximas">
                                    <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden' }}>
                                        {loadingFaturas ? (
                                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                                <Spin />
                                            </div>
                                        ) : proximasFaturas.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                                <Text type="secondary">Nenhuma fatura pendente.</Text>
                                                <div style={{ marginTop: 8 }}>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        Cadastre cartões e lançamentos para acompanhar vencimentos aqui.
                                                    </Text>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Faturas Vencidas */}
                                                {(() => {
                                                    const hoje = new Date();
                                                    hoje.setHours(0, 0, 0, 0);
                                                    const vencidas = proximasFaturas.filter(f => {
                                                        const dataVenc = new Date(f.data_vencimento + 'T00:00:00');
                                                        return dataVenc < hoje;
                                                    });

                                                    if (vencidas.length === 0) return null;

                                                    return (
                                                        <div style={{
                                                            background: '#fff1f0',
                                                            padding: '12px',
                                                            borderRadius: '6px',
                                                            marginBottom: '16px',
                                                            border: '1px solid #ffccc7'
                                                        }}>
                                                            <Text type="danger" strong style={{ fontSize: '13px' }}>
                                                                ⚠️ Vencidas ({vencidas.length})
                                                            </Text>
                                                            <List
                                                                itemLayout="horizontal"
                                                                dataSource={vencidas}
                                                                split={false}
                                                                renderItem={fatura => (
                                                                    <List.Item
                                                                        className="fatura-proxima-item"
                                                                        style={{ padding: '8px 0', cursor: 'pointer' }}
                                                                        onClick={() => navigate(`/cartoes/${fatura.cartao_id}`)}
                                                                    >
                                                                        <List.Item.Meta
                                                                            avatar={<LogoManager slug={fatura.cartao_bandeira} type="bandeira" size={32} />}
                                                                            title={
                                                                                <Text strong ellipsis style={{ maxWidth: 150 }}>{fatura.cartao_nome}</Text>
                                                                            }
                                                                            description={
                                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                                    Venceu {formatDate(fatura.data_vencimento)}
                                                                                </Text>
                                                                            }
                                                                        />
                                                                        <div style={{ textAlign: 'right' }}>
                                                                            <Text strong style={{ fontSize: 16, color: '#D64545' }}>
                                                                                {formatBRL(fatura.valor_total)}
                                                                            </Text>
                                                                        </div>
                                                                    </List.Item>
                                                                )}
                                                            />
                                                        </div>
                                                    );
                                                })()}

                                                {/* Faturas Próximos 7 Dias */}
                                                {(() => {
                                                    const hoje = new Date();
                                                    hoje.setHours(0, 0, 0, 0);
                                                    const seteDiasDepois = new Date(hoje);
                                                    seteDiasDepois.setDate(seteDiasDepois.getDate() + 7);

                                                    const proximas = proximasFaturas.filter(f => {
                                                        const dataVenc = new Date(f.data_vencimento + 'T00:00:00');
                                                        return dataVenc >= hoje && dataVenc <= seteDiasDepois;
                                                    });

                                                    if (proximas.length === 0) return null;

                                                    return (
                                                        <div style={{
                                                            background: '#fffbe6',
                                                            padding: '12px',
                                                            borderRadius: '6px',
                                                            marginBottom: '16px',
                                                            border: '1px solid #ffe58f'
                                                        }}>
                                                            <Text type="warning" strong style={{ fontSize: '13px' }}>
                                                                📅 Próximos 7 dias ({proximas.length})
                                                            </Text>
                                                            <List
                                                                itemLayout="horizontal"
                                                                dataSource={proximas}
                                                                split={false}
                                                                renderItem={fatura => (
                                                                    <List.Item
                                                                        className="fatura-proxima-item"
                                                                        style={{ padding: '8px 0', cursor: 'pointer' }}
                                                                        onClick={() => navigate(`/cartoes/${fatura.cartao_id}`)}
                                                                    >
                                                                        <List.Item.Meta
                                                                            avatar={<LogoManager slug={fatura.cartao_bandeira} type="bandeira" size={32} />}
                                                                            title={
                                                                                <Text strong ellipsis style={{ maxWidth: 150 }}>{fatura.cartao_nome}</Text>
                                                                            }
                                                                            description={
                                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                                    Vence {formatDate(fatura.data_vencimento)}
                                                                                </Text>
                                                                            }
                                                                        />
                                                                        <div style={{ textAlign: 'right' }}>
                                                                            <Text strong style={{ fontSize: 16, color: '#B89040' }}>
                                                                                {formatBRL(fatura.valor_total)}
                                                                            </Text>
                                                                        </div>
                                                                    </List.Item>
                                                                )}
                                                            />
                                                        </div>
                                                    );
                                                })()}

                                                {/* Demais Faturas */}
                                                {(() => {
                                                    const hoje = new Date();
                                                    hoje.setHours(0, 0, 0, 0);
                                                    const seteDiasDepois = new Date(hoje);
                                                    seteDiasDepois.setDate(seteDiasDepois.getDate() + 7);

                                                    const demais = proximasFaturas.filter(f => {
                                                        const dataVenc = new Date(f.data_vencimento + 'T00:00:00');
                                                        return dataVenc > seteDiasDepois;
                                                    });

                                                    if (demais.length === 0) return null;

                                                    return (
                                                        <div>
                                                            <Text type="secondary" strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                                                                Demais faturas ({demais.length})
                                                            </Text>
                                                            <List
                                                                itemLayout="horizontal"
                                                                dataSource={demais}
                                                                split={false}
                                                                renderItem={fatura => (
                                                                    <List.Item
                                                                        className="fatura-proxima-item"
                                                                        style={{ padding: '8px 0', cursor: 'pointer' }}
                                                                        onClick={() => navigate(`/cartoes/${fatura.cartao_id}`)}
                                                                    >
                                                                        <List.Item.Meta
                                                                            avatar={<LogoManager slug={fatura.cartao_bandeira} type="bandeira" size={32} />}
                                                                            title={
                                                                                <Text strong ellipsis style={{ maxWidth: 150 }}>{fatura.cartao_nome}</Text>
                                                                            }
                                                                            description={
                                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                                    Vence {formatDate(fatura.data_vencimento)}
                                                                                </Text>
                                                                            }
                                                                        />
                                                                        <div style={{ textAlign: 'right' }}>
                                                                            <Text strong style={{ fontSize: 16 }}>
                                                                                {formatBRL(fatura.valor_total)}
                                                                            </Text>
                                                                        </div>
                                                                    </List.Item>
                                                                )}
                                                            />
                                                        </div>
                                                    );
                                                })()}
                                            </>
                                        )}
                                    </div>
                                </Card>
                            </div>
                        )}
                    </Col>
                </Row>
            )}

            <CartaoModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                cartaoToEdit={cartaoToEdit}
                isMobile={isMobile}
            />
        </div>
    );
};

export default CartoesPage;